import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShoppingCart, Menu, X, Search, User, ChevronDown } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";

export function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [cartCount, setCartCount] = useState(0);
  const navigate = useNavigate();

  // Mock cart data from localStorage
  useState(() => {
    const cart = JSON.parse(localStorage.getItem("cart") || "[]");
    setCartCount(cart.length);
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue.trim()) {
      navigate(`/search-results?domain=${encodeURIComponent(searchValue.trim())}`);
    }
  };

  const navItems = [
    { name: "Domains", link: "/", hasDropdown: true },
    { name: "Hosting", link: "/hosting/shared", hasDropdown: true },
    { name: "Email", link: "/pricing", hasDropdown: false },
    { name: "Marketing Tools", link: "/marketing", hasDropdown: true },
    { name: "Security", link: "/security", hasDropdown: true },
    { name: "Help Center", link: "/help", hasDropdown: true },
  ];

  const domainDropdownItems = [
    { name: "Domain Search", link: "/domains/search" },
    { name: "Domain Transfer", link: "/domains/transfer" },
    { name: "Domain Pricing", link: "/domains/pricing" },
    { name: "Whois Lookup", link: "/domains/whois" },
  ];

  const hostingDropdownItems = [
    { name: "Shared Hosting", link: "/hosting/shared" },
    { name: "WordPress Hosting", link: "/hosting/wordpress" },
    { name: "Reseller Hosting", link: "/hosting/reseller" },
    { name: "VPS Hosting", link: "/hosting/vps" },
    { name: "Dedicated Servers", link: "/hosting/dedicated" },
    { name: "Migrate to Us", link: "/hosting/migrate" },
  ];

  const securityDropdownItems = [
    { name: "Security Overview", link: "/security" },
    { name: "SSL Certificates", link: "/security/ssl-certificates" },
    { name: "Website Security", link: "/security/website-security" },
    { name: "Domain Privacy", link: "/security/domain-privacy" },
    { name: "FastVPN", link: "/security/fastvpn" },
    { name: "Premium DNS", link: "/security/premium-dns" },
    { name: "Two-Factor Auth", link: "/security/two-factor-auth" },
  ];

  const marketingDropdownItems = [
    { name: "Business Starter Kit", link: "/marketing/business-starter-kit" },
    { name: "Logo Maker", link: "/marketing/logo-maker" },
    { name: "Business Name Generator", link: "/marketing/name-generator" },
    { name: "Site Maker", link: "/marketing/site-maker" },
    { name: "Stencil Graphics", link: "/marketing/stencil-graphics" },
    { name: "Business Card Maker", link: "/marketing/card-maker" },
    { name: "Relate Social AI", link: "/marketing/relate-social" },
    { name: "Relate SEO", link: "/marketing/relate-seo" },
    { name: "Relate Reviews", link: "/marketing/relate-reviews" },
  ];

  const helpCenterDropdownItems = [
    { name: "Knowledgebase", link: "/help/knowledgebase" },
    { name: "Guru Guides", link: "/help/guru-guides" },
    { name: "Blog", link: "/help/blog" },
    { name: "How-To Videos", link: "/help/videos" },
    { name: "Status Updates", link: "/help/status" },
    { name: "Contact Us", link: "/help/contact" },
  ];

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border bg-background/95 backdrop-blur-sm">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="/gictlogo.png" 
                alt="GICT Africa Logo" 
                className="h-8 w-auto"
              />
              <span className="text-lg font-semibold text-foreground hidden sm:block">
                GICT Africa
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-4">
            {navItems.map((item) => (
              <div key={item.name} className="relative">
                {item.hasDropdown ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center text-foreground hover:text-primary transition-colors px-2 py-1 text-sm">
                        {item.name}
                        <ChevronDown className="ml-1 h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="center" className="w-48 mt-1">
                      {(item.name === "Domains" ? domainDropdownItems : 
                        item.name === "Hosting" ? hostingDropdownItems : 
                        item.name === "Marketing Tools" ? marketingDropdownItems :
                        item.name === "Help Center" ? helpCenterDropdownItems :
                        securityDropdownItems).map((dropdownItem) => (
                        <DropdownMenuItem key={dropdownItem.name} asChild>
                          <Link 
                            to={dropdownItem.link}
                            className="w-full cursor-pointer"
                          >
                            {dropdownItem.name}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Link
                    to={item.link}
                    className="text-foreground hover:text-primary transition-colors px-2 py-1 text-sm"
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Desktop Search & Actions */}
          <div className="hidden md:flex items-center space-x-4">
            <form onSubmit={handleSearch} className="relative">
              <Input
                type="text"
                placeholder="Find a domain..."
                className="w-44 lg:w-56 pr-8 h-9 domain-search-input"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
              >
                <Search className="h-4 w-4" />
              </button>
            </form>

            <Link to="/login" className="text-foreground hover:text-primary transition-colors text-sm flex items-center gap-1">
              <User className="h-4 w-4" />
              <span>Sign In</span>
            </Link>

            <Link to="/cart" className="relative">
              <ShoppingCart className="h-5 w-5 text-foreground hover:text-primary transition-colors" />
              {cartCount > 0 && (
                <div className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0 text-[10px] bg-primary text-primary-foreground rounded-full">
                  {cartCount}
                </div>
              )}
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="flex md:hidden items-center gap-4">
            <Link to="/cart" className="relative">
              <ShoppingCart className="h-5 w-5 text-foreground hover:text-primary transition-colors" />
              {cartCount > 0 && (
                <div className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0 text-[10px] bg-primary text-primary-foreground rounded-full">
                  {cartCount}
                </div>
              )}
            </Link>

            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden">
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Toggle menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <div className="px-2">
                  <div className="flex items-center justify-between mb-8">
                    <Link to="/" className="flex items-center gap-2" onClick={() => setIsOpen(false)}>
                      <img 
                        src="/gictlogo.png" 
                        alt="GICT Africa Logo" 
                        className="h-8 w-auto"
                      />
                      <span className="text-lg font-semibold text-foreground">GICT Africa</span>
                    </Link>
                    <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)}>
                      <X className="h-5 w-5" />
                    </Button>
                  </div>

                  {/* Mobile Search */}
                  <form onSubmit={(e) => {
                    handleSearch(e);
                    setIsOpen(false);
                  }} className="relative mb-6">
                    <Input
                      type="text"
                      placeholder="Find a domain..."
                      className="w-full pr-8 domain-search-input"
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                    />
                    <button
                      type="submit"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
                    >
                      <Search className="h-4 w-4" />
                    </button>
                  </form>

                  {/* Mobile Navigation */}
                  <nav className="flex flex-col space-y-4">
                    {navItems.map((item) => (
                      <div key={item.name} className="py-1">
                        {item.hasDropdown ? (
                          <>
                            <div className="flex items-center justify-between py-2 border-b border-border">
                              <span className="font-medium">{item.name}</span>
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <div className="pl-4 mt-2 space-y-2">
                              {(item.name === "Domains" ? domainDropdownItems : 
                                item.name === "Hosting" ? hostingDropdownItems : 
                                item.name === "Marketing Tools" ? marketingDropdownItems :
                                item.name === "Help Center" ? helpCenterDropdownItems :
                                securityDropdownItems).map((dropdownItem) => (
                                <Link
                                  key={dropdownItem.name}
                                  to={dropdownItem.link}
                                  className="block py-1 text-foreground hover:text-primary transition-colors text-sm"
                                  onClick={() => setIsOpen(false)}
                                >
                                  {dropdownItem.name}
                                </Link>
                              ))}
                            </div>
                          </>
                        ) : (
                          <Link
                            to={item.link}
                            className="block py-2 border-b border-border text-foreground hover:text-primary transition-colors"
                            onClick={() => setIsOpen(false)}
                          >
                            {item.name}
                          </Link>
                        )}
                      </div>
                    ))}
                  </nav>

                  {/* Mobile Account */}
                  <div className="mt-6 flex flex-col gap-2">
                    <Button asChild variant="outline" className="w-full justify-start gap-2">
                      <Link to="/login" onClick={() => setIsOpen(false)}>
                        <User className="h-4 w-4" />
                        Sign In
                      </Link>
                    </Button>
                    <Button asChild className="w-full">
                      <Link to="/signup" onClick={() => setIsOpen(false)}>
                        Create Account
                      </Link>
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}